/**
 * DataErrorBanner — Data loading error banner + Demo mode indicator.
 *
 * Shows a friendly notice when an API entity endpoint is unavailable,
 * with expandable error details and a "Demo Mode" badge when using
 * local mock data.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Database, Info } from 'lucide-react';
import { Button } from './button';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DataErrorDetail {
  endpoint?: string;
  status?: number;
  code?: string;
  message?: string;
  raw?: string;
}

interface DataErrorBannerProps {
  error: DataErrorDetail;
  /** friendly title for the module/entity that failed */
  entityLabel?: string;
  /** callback to retry the API fetch */
  onRetry?: () => void;
  /** true = show compact inline variant for mobile */
  compact?: boolean;
}

// ── Banner Component ──────────────────────────────────────────────────────────

export function DataErrorBanner({ error, entityLabel, onRetry, compact }: DataErrorBannerProps) {
  const [expanded, setExpanded] = useState(false);

  // Guard against undefined/null error
  if (!error) return null;

  const friendlyTitle = entityLabel
    ? `${entityLabel}: Data temporarily unavailable`
    : 'Data temporarily unavailable';

  const status = error.status;
  const friendlySubtitle = status === 404
    ? 'Endpoint not available, switched to demo data mode'
    : status === 401 || status === 403
      ? 'Session expired or insufficient permissions, please log in again'
      : status && status >= 500
        ? 'Internal server error, please retry or contact admin'
        : 'Network error or service unavailable, switched to demo data mode';

  if (compact) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        <Database className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
        <span className="flex-1 truncate">Demo Mode · {friendlyTitle}</span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-amber-600 hover:text-amber-800 underline"
        >
          Details
        </button>
        {onRetry && (
          <button onClick={onRetry} className="text-amber-600 hover:text-amber-800">
            <RefreshCw className="h-3 w-3" />
          </button>
        )}
        {expanded && (
          <div className="absolute left-2 right-2 mt-16 z-50 rounded-lg bg-white border border-amber-200 shadow-lg p-3 text-xs space-y-1">
            <ErrorDetailRows error={error} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-amber-50/80 border border-amber-200 overflow-hidden">
      {/* Main row */}
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-amber-900 text-sm">{friendlyTitle}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-200/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              <Database className="h-2.5 w-2.5" />
              Demo
            </span>
          </div>
          <p className="mt-0.5 text-xs text-amber-700/80 leading-relaxed">{friendlySubtitle}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {onRetry && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRetry}
              className="h-7 gap-1 text-xs text-amber-700 hover:bg-amber-100"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="h-7 gap-1 text-xs text-amber-700 hover:bg-amber-100"
          >
            <Info className="h-3 w-3" />
            {expanded ? 'Collapse' : 'Details'}
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      {/* Expandable detail */}
      {expanded && (
        <div className="border-t border-amber-200/60 bg-amber-50/50 px-4 py-3">
          <div className="rounded-lg bg-white/80 border border-amber-100 p-3">
            <p className="text-xs font-medium text-amber-800 mb-2">Error Details</p>
            <ErrorDetailRows error={error} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail rows ───────────────────────────────────────────────────────────────

function ErrorDetailRows({ error }: { error: DataErrorDetail }) {
  const rows: [string, string][] = [];
  if (error.endpoint) rows.push(['Endpoint', error.endpoint]);
  if (error.status != null) rows.push(['HTTP Status', `${error.status} ${httpStatusText(error.status)}`]);
  if (error.code) rows.push(['Error Code', error.code]);
  if (error.message) rows.push(['Error Message', error.message]);
  if (error.raw) rows.push(['Raw Error', error.raw]);

  if (rows.length === 0) {
    rows.push(['Cause', 'Unknown error, no further details available']);
  }

  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <span className="text-[11px] font-mono text-amber-600 whitespace-nowrap">{label}</span>
          <span className="text-[11px] font-mono text-gray-700 break-all">{value}</span>
        </div>
      ))}
    </div>
  );
}

function httpStatusText(status: number): string {
  const map: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    408: 'Request Timeout',
    422: 'Unprocessable Entity',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return map[status] ?? '';
}
